/* ============================================================
   OVESSI Motion Layer v1 — controller
   Activates animations only when:
     - user does not prefer reduced motion
     - element is sufficiently in view (mobile) OR
       receives deliberate hover/focus (desktop on cards)
   Enforces "one active card at a time" for rituals + categories.
   ============================================================ */
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (REDUCED) {
    // Still flip reveals on so the page reads naturally for AT users
    var els = document.querySelectorAll('[data-ovssm-reveal]');
    for (var i = 0; i < els.length; i++) els[i].classList.add('ovssm-in');
    return;
  }
  if (!('IntersectionObserver' in window)) return;

  var IS_TOUCH = window.matchMedia && window.matchMedia('(hover: none)').matches;

  /* ---------------- Reveal ---------------- */
  var revealEls = document.querySelectorAll('[data-ovssm-reveal]');
  if (revealEls.length) {
    var revealIo = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('ovssm-in');
          revealIo.unobserve(e.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -60px 0px' });
    revealEls.forEach(function (el) { revealIo.observe(el); });
  }

  /* ---------------- Count-up for the Skin AI report ---------------- */
  function countUp(el, target, ms) {
    var t0 = null;
    var startVal = 0;
    var isPct = /%/.test(el.textContent || '');
    function tick(t) {
      if (!t0) t0 = t;
      var p = Math.min(1, (t - t0) / ms);
      // ease-out cubic
      var eased = 1 - Math.pow(1 - p, 3);
      var v = Math.round(startVal + (target - startVal) * eased);
      if (isPct) {
        el.innerHTML = v + '<em>%</em>';
      } else {
        el.textContent = String(v);
      }
      if (p < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }
  function activateStats(panel) {
    if (panel.dataset.ovssmStatsRun === '1') return;
    panel.dataset.ovssmStatsRun = '1';
    // Stash original values, zero them, then count up.
    var stats = panel.querySelectorAll('.luv-skinai-preview-stat-value, .luv-skinai-preview-bar-val');
    stats.forEach(function (s) {
      var raw = (s.textContent || '').trim();
      var m = raw.match(/(\d+)/);
      if (!m) return;
      var target = parseInt(m[1], 10);
      s.dataset.ovssmTarget = String(target);
    });
    // Read the bar fill widths, then set as CSS var and zero them in advance
    var fills = panel.querySelectorAll('.luv-skinai-preview-bar-fill');
    fills.forEach(function (f) {
      var styleW = f.getAttribute('style') || '';
      var mw = styleW.match(/width\s*:\s*(\d+)%/i);
      if (mw) {
        f.style.setProperty('--ovssm-w', mw[1] + '%');
      }
    });
    panel.classList.add('ovssm-stats-in');
    // Stagger the count-ups
    setTimeout(function () {
      stats.forEach(function (s, idx) {
        var t = parseInt(s.dataset.ovssmTarget || '0', 10);
        setTimeout(function () { countUp(s, t, 1400); }, idx * 80);
      });
    }, 240);
  }

  var statPanels = document.querySelectorAll('.luv-skinai-preview');
  if (statPanels.length) {
    statPanels.forEach(function (p) { p.setAttribute('data-ovssm-stats', ''); });
    var statsIo = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          activateStats(e.target);
          statsIo.unobserve(e.target);
        }
      });
    }, { threshold: 0.35 });
    statPanels.forEach(function (p) { statsIo.observe(p); });
  }

  /* ---------------- Skin AI panel scan glow ---------------- */
  var aiPanels = document.querySelectorAll('.luv-skinai-panel');
  if (aiPanels.length) {
    var panelIo = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('ovssm-scan-in');
        } else {
          e.target.classList.remove('ovssm-scan-in');
        }
      });
    }, { threshold: 0.25 });
    aiPanels.forEach(function (p) { panelIo.observe(p); });
  }

  /* ---------------- One-active-card scheduler ----------------
     Mobile: card centred in viewport is "active".
     Desktop: deliberate hover/focus is "active".
     Only ONE card is ever active at a time per group. */

  function setupOneActive(selector, groupName) {
    var cards = document.querySelectorAll(selector);
    if (!cards.length) return;
    var active = null;
    function setActive(card) {
      if (active === card) return;
      if (active) active.classList.remove('ovssm-card-active');
      active = card || null;
      if (active) active.classList.add('ovssm-card-active');
    }
    if (IS_TOUCH) {
      // Pick the card whose centre is closest to viewport centre, when group is in view.
      var groupIo = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) e.target.dataset.ovssmInGroup = '1';
          else e.target.dataset.ovssmInGroup = '0';
        });
      }, { threshold: 0.05 });
      cards.forEach(function (c) { groupIo.observe(c); });
      function pick() {
        var vh = window.innerHeight;
        var vc = vh / 2;
        var best = null;
        var bestDist = Infinity;
        cards.forEach(function (c) {
          if (c.dataset.ovssmInGroup !== '1') return;
          var r = c.getBoundingClientRect();
          if (r.bottom < 0 || r.top > vh) return;
          var d = Math.abs((r.top + r.height / 2) - vc);
          if (d < bestDist) { bestDist = d; best = c; }
        });
        setActive(best);
      }
      var raf = 0;
      function onScroll() {
        if (raf) return;
        raf = requestAnimationFrame(function () { raf = 0; pick(); });
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      pick();
    } else {
      // Desktop: deliberate hover/focus on a card activates it; mouseleave deactivates.
      cards.forEach(function (c) {
        var enterTimer = null;
        c.addEventListener('mouseenter', function () {
          enterTimer = setTimeout(function () { setActive(c); }, 80);
        });
        c.addEventListener('mouseleave', function () {
          if (enterTimer) { clearTimeout(enterTimer); enterTimer = null; }
          if (active === c) setActive(null);
        });
        c.addEventListener('focusin', function () { setActive(c); });
        c.addEventListener('focusout', function (ev) {
          if (!c.contains(ev.relatedTarget)) {
            if (active === c) setActive(null);
          }
        });
      });
    }
  }
  setupOneActive('.luv-card', 'rituals');
  setupOneActive('.luv-sbc-tile', 'categories');
})();

;(function(){
  /* ---------------- Offers hero video controller ----------------
     Plays the video only when:
       - its slide has class .is-active (slideshow is on this slide), AND
       - the slideshow is in the viewport
     Pauses otherwise. Original poster image remains visible behind. */
  if (typeof window === 'undefined') return;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  var videos = document.querySelectorAll('[data-luv-offers-video]');
  if (!videos.length) return;

  var slider = document.querySelector('.luv-offers-slider');
  var sliderInView = false;

  if (slider && 'IntersectionObserver' in window) {
    var sliderIo = new IntersectionObserver(function(entries){
      entries.forEach(function(e){ sliderInView = e.isIntersecting; sync(); });
    }, { threshold: 0.1 });
    sliderIo.observe(slider);
  } else {
    sliderInView = true;
  }

  function sync(){
    videos.forEach(function(v){
      var slide = v.closest('.luv-offers-slide');
      var active = slide && slide.classList.contains('is-active');
      if (active && sliderInView) {
        if (v.paused) {
          var p = v.play();
          if (p && typeof p.catch === 'function') p.catch(function(){});
        }
      } else {
        if (!v.paused) v.pause();
      }
    });
  }

  // Mutation observer on each slide's class so we react to slideshow controller
  if ('MutationObserver' in window) {
    videos.forEach(function(v){
      var slide = v.closest('.luv-offers-slide');
      if (!slide) return;
      var mo = new MutationObserver(sync);
      mo.observe(slide, { attributes: true, attributeFilter: ['class'] });
    });
  }

  sync();
})();
